import React, { useState, useEffect } from 'react';
import { 
  GitBranch, Plus, Trash2, Save, GripVertical, ChevronRight, 
  Loader2, AlertCircle, CheckCircle, Settings, Users
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { adminApi } from '@/lib/api';

const AVAILABLE_ROLES = [
  { value: 'company_admin', label: 'Administrador' },
  { value: 'compliance_officer', label: 'Compliance Officer' },
  { value: 'triage_manager', label: 'Gestor Triagem' },
  { value: 'case_manager', label: 'Gestor Caso' },
  { value: 'dpo', label: 'DPO' }
];

const DEFAULT_STAGES = [
  { stage: 'TRIAGEM', name: 'Triagem', roles: ['compliance_officer', 'triage_manager'], required: true },
  { stage: 'INVESTIGACAO', name: 'Investigacao', roles: ['case_manager', 'compliance_officer'], required: false },
  { stage: 'APROVACAO', name: 'Aprovacao Final', roles: ['company_admin', 'compliance_officer'], required: true }
];

const WorkflowConfigPage = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [hasChanges, setHasChanges] = useState(false);

  const [formData, setFormData] = useState({
    name: 'Workflow Padrao',
    stages: DEFAULT_STAGES,
    is_active: true
  });

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await adminApi.getWorkflowConfig();
      setConfig(response.data);
      setFormData({
        name: response.data.name || 'Workflow Padrao',
        stages: response.data.stages || DEFAULT_STAGES,
        is_active: response.data.is_active !== false
      });
    } catch (err) {
      // Use defaults if no config exists
      setFormData({
        name: 'Workflow Padrao',
        stages: DEFAULT_STAGES,
        is_active: true
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (formData.stages.filter(s => s.required).length < 1) {
      setError('E necessario pelo menos uma etapa obrigatoria');
      return;
    }

    setSaving(true);
    setError('');

    try {
      await adminApi.updateWorkflowConfig(formData);
      setSuccess('Workflow guardado com sucesso');
      setHasChanges(false);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao guardar workflow');
    } finally {
      setSaving(false);
    }
  };

  const addStage = () => {
    const newStage = {
      stage: `ETAPA_${formData.stages.length + 1}`,
      name: `Nova Etapa ${formData.stages.length + 1}`,
      roles: ['compliance_officer'],
      required: false
    };
    
    setFormData(prev => ({
      ...prev,
      stages: [...prev.stages, newStage]
    }));
    setHasChanges(true);
  };

  const removeStage = (index) => {
    if (formData.stages.length <= 1) {
      setError('E necessario pelo menos uma etapa');
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      stages: prev.stages.filter((_, i) => i !== index)
    }));
    setHasChanges(true);
  };

  const updateStage = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      stages: prev.stages.map((stage, i) => 
        i === index ? { ...stage, [field]: value } : stage
      )
    }));
    setHasChanges(true);
  };

  const toggleRole = (stageIndex, role) => {
    const stage = formData.stages[stageIndex];
    const newRoles = stage.roles.includes(role)
      ? stage.roles.filter(r => r !== role)
      : [...stage.roles, role];
    
    if (newRoles.length === 0) {
      setError('Selecione pelo menos um perfil');
      return;
    }
    
    updateStage(stageIndex, 'roles', newRoles);
  };

  const moveStage = (fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= formData.stages.length) return;
    
    const newStages = [...formData.stages];
    const [removed] = newStages.splice(fromIndex, 1);
    newStages.splice(toIndex, 0, removed);
    
    setFormData(prev => ({ ...prev, stages: newStages }));
    setHasChanges(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
            Configuracao de Workflow
          </h1>
          <p className="text-slate-500 mt-1">
            Configure as etapas de aprovacao para denuncias
          </p>
        </div>
        
        <Button onClick={handleSave} disabled={saving || !hasChanges} data-testid="save-workflow-btn">
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              A guardar...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Guardar Alteracoes
            </>
          )}
        </Button>
      </div>

      {/* Alerts */}
      {error && (
        <Alert className="bg-red-50 border-red-200">
          <AlertCircle className="w-4 h-4 text-red-600" />
          <AlertDescription className="text-red-700">{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="bg-emerald-50 border-emerald-200">
          <CheckCircle className="w-4 h-4 text-emerald-600" />
          <AlertDescription className="text-emerald-700">{success}</AlertDescription>
        </Alert>
      )}

      {/* Basic Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Configuracoes Gerais
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="workflow-name">Nome do Workflow</Label>
              <Input
                id="workflow-name"
                value={formData.name}
                onChange={(e) => {
                  setFormData(prev => ({ ...prev, name: e.target.value }));
                  setHasChanges(true);
                }}
                placeholder="Workflow Padrao"
              />
            </div>
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div>
                <p className="font-medium text-slate-900">Workflow Ativo</p>
                <p className="text-sm text-slate-500">Ativar aprovacoes multi-nivel</p>
              </div>
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => {
                  setFormData(prev => ({ ...prev, is_active: checked }));
                  setHasChanges(true);
                }}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Workflow Stages */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <GitBranch className="w-5 h-5" />
                Etapas do Workflow
              </CardTitle>
              <CardDescription>
                Defina as etapas de aprovacao e os perfis responsaveis
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={addStage} data-testid="add-stage-btn">
              <Plus className="w-4 h-4 mr-2" />
              Adicionar Etapa
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {formData.stages.map((stage, index) => (
              <div 
                key={index}
                className="border rounded-lg p-4 bg-white hover:shadow-sm transition-shadow"
                data-testid={`stage-${index}`}
              >
                <div className="flex items-start gap-4">
                  {/* Drag Handle & Order */}
                  <div className="flex flex-col items-center gap-1 pt-2">
                    <GripVertical className="w-5 h-5 text-slate-300 cursor-grab" />
                    <div className="flex flex-col gap-1">
                      <button 
                        onClick={() => moveStage(index, index - 1)}
                        disabled={index === 0}
                        className="text-slate-400 hover:text-slate-600 disabled:opacity-30"
                      >
                        ▲
                      </button>
                      <button 
                        onClick={() => moveStage(index, index + 1)}
                        disabled={index === formData.stages.length - 1}
                        className="text-slate-400 hover:text-slate-600 disabled:opacity-30"
                      >
                        ▼
                      </button>
                    </div>
                  </div>

                  {/* Stage Number */}
                  <div className="w-10 h-10 rounded-full bg-slate-900 text-white flex items-center justify-center font-bold">
                    {index + 1}
                  </div>

                  {/* Stage Content */}
                  <div className="flex-1 space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Identificador</Label>
                        <Input
                          value={stage.stage}
                          onChange={(e) => updateStage(index, 'stage', e.target.value.toUpperCase().replace(/\s/g, '_'))}
                          className="font-mono text-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Nome da Etapa</Label>
                        <Input
                          value={stage.name}
                          onChange={(e) => updateStage(index, 'name', e.target.value)}
                        />
                      </div>
                    </div>

                    {/* Roles */}
                    <div>
                      <Label className="text-xs flex items-center gap-1 mb-2">
                        <Users className="w-3 h-3" />
                        Perfis com Permissao
                      </Label>
                      <div className="flex flex-wrap gap-2">
                        {AVAILABLE_ROLES.map(({ value, label }) => (
                          <label
                            key={value}
                            className={`flex items-center gap-2 px-3 py-1.5 rounded-full border cursor-pointer transition-colors ${
                              stage.roles.includes(value)
                                ? 'bg-slate-900 text-white border-slate-900'
                                : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
                            }`}
                          >
                            <Checkbox
                              checked={stage.roles.includes(value)}
                              onCheckedChange={() => toggleRole(index, value)}
                              className="hidden"
                            />
                            <span className="text-sm">{label}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Required Toggle */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id={`required-${index}`}
                          checked={stage.required}
                          onCheckedChange={(checked) => updateStage(index, 'required', checked)}
                        />
                        <Label htmlFor={`required-${index}`} className="text-sm cursor-pointer">
                          Etapa obrigatoria
                        </Label>
                      </div>
                      
                      {stage.required && (
                        <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                          Obrigatoria
                        </Badge>
                      )}
                    </div>
                  </div>

                  {/* Delete Button */}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeStage(index)}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    disabled={formData.stages.length <= 1}
                    data-testid={`delete-stage-${index}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>

                {/* Connector */}
                {index < formData.stages.length - 1 && (
                  <div className="flex justify-center mt-4">
                    <ChevronRight className="w-6 h-6 text-slate-300 rotate-90" />
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Visual Flow */}
          <div className="mt-6 pt-6 border-t">
            <p className="text-sm font-medium text-slate-700 mb-3">Fluxo Visual:</p>
            <div className="flex items-center gap-2 overflow-x-auto pb-2">
              <Badge variant="outline" className="whitespace-nowrap">Inicio</Badge>
              {formData.stages.map((stage, index) => (
                <React.Fragment key={index}>
                  <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
                  <Badge 
                    className={`whitespace-nowrap ${stage.required ? 'bg-slate-900' : 'bg-slate-500'}`}
                  >
                    {stage.name}
                  </Badge>
                </React.Fragment>
              ))}
              <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
              <Badge variant="outline" className="whitespace-nowrap bg-emerald-50 text-emerald-700 border-emerald-200">
                Concluido
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default WorkflowConfigPage;
